
<script setup>
import {computed, watch, ref} from 'vue'
import { storeToRefs } from 'pinia'
import moment from 'moment'
import useStore from './../store'

// Props definition
const props = defineProps({
  showTitle: {
    type: Boolean,
    default: false
  },
  quote: {
    type: Object,
    default: () => ({})
  }
})

// Store
const store = useStore()
const { totalPeople, attractions, items } = storeToRefs(store)

// Computed properties
const quoteItems = computed(() => props.quote?.items || [])
const itemsWithAttraction = computed(() => store.items.filter(i => i.attraction))

const discountValue = computed(() => {
  if (!props.quote?.items || props.quote.totalPaid === 0) {
    return false
  }

  return ` AUD -$${(props.quote.totalPaid).toFixed(2)}`
})

// Methods
const humanTime = (time) => {
  return moment(time).format("dddd Do MMMM YYYY, h:mma")
}

const itemTotal = (price, qty) => {
  return (price * qty).toFixed(2)
}

const attractionFromProductCode = (productCode) => {
  return store.attractions.length > 0 ? store.attractions.find(a => a.rezdy_retail_product_code === productCode) : null;
}

const attractionFromId = (attractionId) => {
  return store.attractions.length > 0 ? store.attractions.find(a => a.id === attractionId) : null;
}

</script>

<template>
  <div>
    <div class="pb-4 md:py-4 mb-6" v-if="itemsWithAttraction.length > 0 || quoteItems.length > 0">
      <div class="bg-light rounded-lg relative px-4 py-6 pt-10">
        <span
            class="h-12 w-12 absolute left-4 -top-6 bg-primary text-light rounded-full inline-flex justify-center items-center leading-none">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1.80598 10.0918L15.4459 2.2168C16.5115 1.60156 17.9042 1.97474 18.5194 3.04035L19.5917 4.89757C19.7675 5.20203 19.7126 5.5991 19.4913 5.84864C19.0359 6.39575 18.9565 7.17229 19.308 7.78122C19.6772 8.42059 20.3894 8.7401 21.0909 8.61924C21.4176 8.55238 21.789 8.70335 21.9647 9.00781L23.0194 10.8346C23.6522 11.9306 23.2615 13.2929 22.1959 13.9081L8.55598 21.7831C7.45992 22.416 6.11523 22.0556 5.48242 20.9596L4.42773 19.1328C4.25195 18.8283 4.27643 18.4489 4.52815 18.1817C4.95314 17.6522 5.06298 16.8581 4.69384 16.2187C4.34228 15.6098 3.59962 15.3079 2.92855 15.4111C2.57135 15.4956 2.23047 15.327 2.05469 15.0226L0.982422 13.1654C0.367188 12.0997 0.709916 10.7246 1.80598 10.0918ZM2.44384 12.3216L3.23486 13.6917C4.38618 13.7171 5.54002 14.3094 6.15526 15.375C6.78807 16.471 6.72407 17.7664 6.1704 18.7762L6.94384 20.1158C7.10204 20.3898 7.43821 20.4799 7.71223 20.3217L21.3521 12.4467C21.5957 12.3061 21.7162 11.9523 21.558 11.6783L20.7846 10.3387C19.6028 10.3309 18.4794 9.72103 17.8466 8.62497C17.2314 7.55935 17.2649 6.28153 17.849 5.25418L17.058 3.8841C16.9174 3.64053 16.5332 3.53759 16.2896 3.67821L2.64973 11.5532C2.37571 11.7114 2.30321 12.078 2.44384 12.3216ZM16.2547 11.4926L14.5672 8.5698L7.7472 12.5073L9.4347 15.4301L16.2547 11.4926ZM6.00454 12.8639C5.70571 12.3463 5.86828 11.6435 6.41631 11.3271L14.2105 6.82713C14.7281 6.5283 15.4485 6.72133 15.7473 7.23891L17.9973 11.136C18.3137 11.6841 18.1031 12.374 17.5855 12.6728L9.79131 17.1728C9.24328 17.4892 8.57094 17.3091 8.25454 16.761L6.00454 12.8639Z"
                  fill="currentColor"/>
          </svg>
        </span>
        <ul class="list-none flex flex-col gap-4" v-if="quoteItems.length > 0">
          <li v-for="item in quoteItems" :key="item.id">
            <h4 class="text-xlx font-semibold">{{ attractionFromProductCode(item.productCode)?.retail_title ?? item.productName }}, {{ attractionFromProductCode(item.productCode)?.retail_location ?? '' }}</h4>
            <p class="text-dark mt-2">{{ humanTime(item.startTimeLocal) }}</p>
            <p class="mt-2 flex gap-2">
              <svg class="w-4 h-5 text-primary" width="16" height="19" viewBox="0 0 16 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.28571 1.72244L15.4643 6.54883C15.7857 6.653 16 6.9655 16 7.278C16 7.62522 15.7857 7.93772 15.4643 8.04188L2.28571 12.8336V17.278C2.28571 17.903 1.75 18.3891 1.14286 18.3891C0.5 18.3891 0 17.903 0 17.278V1.72244C0 1.13216 0.5 0.611328 1.14286 0.611328C1.75 0.611328 2.28571 1.13216 2.28571 1.72244Z"
                      fill="currentColor"/>
              </svg>
              {{ attractionFromProductCode(item.productCode)?.retail_meta ?? '' }}
            </p>
          </li>
        </ul>
        <ul class="list-none flex flex-col gap-4" v-else-if="itemsWithAttraction.length > 0">
          <li v-for="item in itemsWithAttraction" :key="item.id">
            <h4 class="text-xlx font-semibold">{{ attractionFromId(item.attraction)?.retail_title ?? '' }}, {{ attractionFromId(item.attraction)?.retail_location ?? '' }}</h4>
            <p class="text-dark mt-2" v-if="item.time">{{ humanTime(item.time) }}</p>
            <p class="mt-2 flex gap-2">
              <svg class="w-4 h-5 text-primary" width="16" height="19" viewBox="0 0 16 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.28571 1.72244L15.4643 6.54883C15.7857 6.653 16 6.9655 16 7.278C16 7.62522 15.7857 7.93772 15.4643 8.04188L2.28571 12.8336V17.278C2.28571 17.903 1.75 18.3891 1.14286 18.3891C0.5 18.3891 0 17.903 0 17.278V1.72244C0 1.13216 0.5 0.611328 1.14286 0.611328C1.75 0.611328 2.28571 1.13216 2.28571 1.72244Z"
                      fill="currentColor"/>
              </svg>
              {{ attractionFromId(item.attraction)?.retail_meta ?? '' }}
            </p>
          </li>
        </ul>
      </div>
      <ul class="list-none" v-if="quoteItems.length > 0">
        <li v-for="item in quoteItems" :key="item.id" class="lg:p-2">
          <ul class="text-dark mb-0">
            <li
                v-for="(quantity, ind) in item.quantities"
                :key="`${item.id}-${ind}`"
                aria-label="price item"
                class="flex py-2 text-dark flex-wrap"
            >
              <div class="w-1/2">
                <div
                    class="font-bold"
                    :title="`$${quantity.optionPrice.toFixed(2)}`"
                >
                  {{ `${quantity.value}x ${quantity.optionLabel}` }}
                </div>
                <div class="text-base text-dark">{{ attractionFromProductCode(item.productCode)?.retail_title ?? item.productName }}</div>
              </div>
              <div class="w-1/2 text-right">
                {{ `$${itemTotal(quantity.optionPrice, quantity.value)}` }}
              </div>
            </li>
            <li
                v-for="(extra, ind) in item.extras"
                :key="`${item.id}-${ind}-extra`"
                aria-label="price item"
                class="flex py-2 text-dark flex-wrap"
            >
              <div class="w-1/2">
                <div class="text-base text-dark">{{ `${extra.quantity}x ${extra.name}` }}</div>
              </div>
              <div class="w-1/2 text-right">
                {{ `$${itemTotal(extra.price, extra.quantity)}` }}
              </div>
            </li>
          </ul>
        </li>
      </ul>
      <div v-if="discountValue" class="flex justify-between mb-2 pt-2 mt-4 border-t border-dotted border-primary">
        <div>Discount</div>
        <div v-html="discountValue"></div>
      </div>
    </div>
    <div class="flex justify-between items-center border-t border-b border-dotted border-primary py-4 mt-4 mb-6" v-if="quoteItems.length > 0">
      <div class="flex flex-col lg:flex-row gap-1">
        <strong>Total</strong>
        <span>(inc GST)</span>
      </div>
      <div class="text-xl">{{ `AUD $${(quote?.totalDue ?? 0).toFixed(2)}` }}</div>
    </div>
  </div>
</template>
